import * as React from "react";
import { Search, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { ListItem } from "../ListItem";
import { Button } from "../Button";
import { Divider } from "../Divider";
import { Text } from "../Text";
import { Link } from "../Link";

export interface DropdownMenuItem {
  value: string | number;
  label: string;
  description?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  isDisabled?: boolean;
  onClick?: () => void;
  variant?: "default" | "bordered" | "primary" | "negative";
}

export interface DropdownMenuProps {
  /**
   * Items to display in the dropdown
   */
  items?: DropdownMenuItem[];
  /**
   * Custom content to render instead of the default item list
   */
  customContent?: React.ReactNode;
  /**
   * Section heading text
   */
  customContentClassName?: string;
  sectionHeading?: string;
  /**
   * Whether the dropdown is in loading state
   */
  isLoading?: boolean;
  /**
   * Whether to show empty/no results state
   */
  isEmpty?: boolean;
  /**
   * Empty state title
   */
  emptyTitle?: string;
  /**
   * Empty state description
   */
  emptyDescription?: string;
  /**
   * Empty state link text
   */
  emptyLinkText?: string;
  /**
   * Empty state link click handler
   */
  onEmptyLinkClick?: () => void;
  /**
   * Primary button text
   */
  primaryButtonText?: string;
  /**
   * Secondary button text
   */
  secondaryButtonText?: string;
  /**
   * Primary button click handler
   */
  onPrimaryClick?: () => void;
  /**
   * Secondary button click handler
   */
  onSecondaryClick?: () => void;
  /**
   * Show chevron on list items
   */
  showChevron?: boolean;
  /**
   * Custom empty state icon
   */
  emptyIcon?: React.ReactNode;
  /**
   * Disable footer buttons
   */
  disableFooter?: boolean;
  /**
   * Whether to show the footer actions (overrides disableFooter)
   */
  showFooter?: boolean;
  /**
   * Footer layout orientation
   */
  footerLayout?: "horizontal" | "vertical";
  /**
   * Callback when menu should close
   */
  onClose?: () => void;
  /**
   * Focused item index (for keyboard navigation)
   */
  focusedIndex?: number;
  /**
   * Custom class name
   */
  className?: string;
  /**
   * Width of the dropdown menu
   */
  width?: "auto" | "full" | string;
  /**
   * Max height of the dropdown menu (used for viewport constraint)
   */
  maxHeight?: string;
  /**
   * Whether to remove container styling (useful when nested in BottomSheet)
   */
  unstyled?: boolean;
}

export const DropdownMenu = React.forwardRef<HTMLDivElement, DropdownMenuProps>(
  (
    {
      items = [],
      customContent,
      customContentClassName,
      sectionHeading,
      isLoading = false,
      isEmpty = false,
      emptyTitle = "No Search Results Found",
      emptyDescription = "Add description of what the user can search for here.",
      emptyLinkText = "Link to support site",
      onEmptyLinkClick,
      primaryButtonText = "Primary",
      secondaryButtonText = "Secondary",
      onPrimaryClick,
      onSecondaryClick,
      showChevron = false,
      emptyIcon,
      disableFooter = false,
      showFooter,
      footerLayout = "horizontal",
      onClose,
      focusedIndex = -1,
      className,
      width = "auto",
      maxHeight = "400px",
      unstyled = false,
    },
    ref
  ) => {
    const renderContent = () => {
      if (isLoading) {
        return (
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <Loader2 className="w-12 h-12 text-action-ink-primary-normal mb-4 animate-spin" />
          </div>
        );
      }

      if (customContent) {
        return (
          <div
            className={`py-3 px-3 overflow-y-auto ${customContentClassName}`}
            style={{ maxHeight }}
          >
            {sectionHeading && (
              <Text
                as="div"
                variant="body"
                size="small"
                weight="medium"
                className="text-body-small-medium text-surface-ink-neutral-muted px-3 py-2 mb-1"
              >
                {sectionHeading}
              </Text>
            )}
            <div>{customContent}</div>
          </div>
        );
      }

      if (isEmpty || items.length === 0) {
        return (
          <div className="flex flex-col items-center justify-center py-8 px-6 text-center">
            {emptyIcon || (
              <Search className="w-12 h-12 text-surface-ink-neutral-muted mb-4" />
            )}
            <Text
              as="h3"
              variant="body"
              size="small"
              weight="semibold"
              className="text-surface-ink-neutral-normal mb-2"
            >
              {emptyTitle}
            </Text>
            <Text
              as="p"
              variant="body"
              size="small"
              weight="regular"
              className="text-surface-ink-neutral-muted mb-3"
            >
              {emptyDescription}
            </Text>
            {emptyLinkText && (
              <Link
                type="anchor"
                color="primary"
                size="small"
                onClick={onEmptyLinkClick}
              >
                {emptyLinkText}
              </Link>
            )}
          </div>
        );
      }

      return (
        <div className={`py-3 px-3 overflow-y-auto`} style={{ maxHeight }}>
          {sectionHeading && (
            <Text
              as="div"
              variant="body"
              size="small"
              weight="medium"
              className="text-surface-ink-neutral-muted px-3 py-2 mb-1"
            >
              {sectionHeading}
            </Text>
          )}
          <div className="flex flex-col gap-1">
            {items.map((item, index) => (
              <ListItem
                key={item.value}
                title={item.label}
                description={item.description}
                leadingIcon={item.leadingIcon}
                trailingIcon={item.trailingIcon}
                showChevron={showChevron}
                isDisabled={item.isDisabled}
                isSelected={index === focusedIndex}
                variant={item.variant}
                onClick={() => {
                  item.onClick?.();
                  onClose?.();
                }}
                containerClassName={cn(
                  index === focusedIndex && "bg-action-fill-primary-faded"
                )}
              />
            ))}
          </div>
        </div>
      );
    };

    const widthClass =
      width === "full" ? "w-full" : width === "auto" ? "w-auto" : "";
    const footerVisible = showFooter ?? !disableFooter;

    return (
      <div
        ref={ref}
        className={cn(
          !unstyled && "bg-white rounded-large overflow-hidden",
          unstyled && "w-full",
          widthClass,
          className
        )}
        style={{
          ...(!unstyled && {
            boxShadow:
              "0 1px 2px rgba(25, 25, 30, 0.1), 0 2px 6px rgba(25, 25, 30, 0.06)",
          }),
          ...(width !== "full" && width !== "auto" ? { width } : {}),
        }}
      >
        {/* Content */}
        {renderContent()}

        {/* Footer */}
        {footerVisible && (
          <div className="flex flex-col">
            <Divider thickness="thin" variant="muted" />
            <div
              className={cn(
                "flex gap-3 p-4",
                footerLayout === "vertical"
                  ? "flex-col"
                  : "items-center flex-row"
              )}
            >
              {onSecondaryClick && (
                <Button
                  variant="secondary"
                  color="primary"
                  size="medium"
                  isFullWidth
                  onClick={onSecondaryClick}
                >
                  {secondaryButtonText}
                </Button>
              )}
              {onPrimaryClick && (
                <Button
                  variant="primary"
                  color="primary"
                  size="medium"
                  isFullWidth
                  onClick={onPrimaryClick}
                >
                  {primaryButtonText}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

DropdownMenu.displayName = "DropdownMenu";
